import React, { useEffect, useRef, useState } from "react";
import { X, Mic, MicOff, Loader2, PhoneOff, DatabaseZap, Lock, FileText, CornerUpLeft } from "lucide-react";
import KnowledgeBaseInput from "../KnowledgeBase/KnowledgeBaseInput";
import HatInput from "../Hat/HatInput";
import { useKnowledgeBase } from "../../context/KnowledgeBaseContext";
import { useHat } from "../../context/HatContext";
import { VoiceBrainBankSource, VoiceBrainBankState, VoiceTranscriptEntry } from "../../types";

interface VoiceModeModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The conversation is live: the mic streams until it ends. */
  isRecording: boolean;
  /** The live mic is muted (Sonic hears silence). */
  isMuted?: boolean;
  isSpeaking: boolean;
  isConnecting: boolean;
  isThinking: boolean;
  onToggleRecording: () => void;
  onEndVoiceMode: () => void;

  // Live transcript: the turn in progress and the messages saved so far
  userText?: string;
  assistantText?: string;
  conversationHistory?: VoiceTranscriptEntry[];

  // BrainBank state reported by the voice backend
  voiceBrainBank?: VoiceBrainBankState;
}

type Phase = "ready" | "connecting" | "muted" | "searching" | "speaking" | "thinking" | "listening";

const formatElapsed = (seconds: number) =>
  `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;

// The documents an answer came from, once each, in the order they ranked.
const SourceList: React.FC<{ sources: VoiceBrainBankSource[] }> = ({ sources }) => {
  const names = Array.from(new Set(sources.map((source) => source.fileName)));
  return (
    <div className="flex flex-wrap gap-1.5 pl-1">
      {names.map((name) => (
        <span
          key={name}
          title={name}
          className="inline-flex items-center gap-1.5 h-[26px] max-w-[240px] px-2.5 rounded-full border border-border-default text-xs text-muted"
        >
          <FileText size={12} className="flex-shrink-0 text-violet-700 dark:text-violet-300" />
          <span className="truncate">{name}</span>
        </span>
      ))}
    </div>
  );
};

// Animated level bars: violet while Kyva listens, teal while it speaks.
const LevelBars: React.FC<{ tone: "listen" | "speak"; count: number }> = ({ tone, count }) => {
  const heights = [16, 30, 42, 24, 36, 20, 30, 18, 28];
  return (
    <div aria-hidden="true" className="h-11 flex items-center gap-[5px]">
      {heights.slice(0, count).map((height, i) => (
        <span
          key={i}
          className={`w-[5px] rounded-full animate-pulse ${tone === "listen" ? "bg-violet-700 dark:bg-violet-400" : "bg-cyan-700 dark:bg-cyan-400"}`}
          style={{ height, animationDelay: `${i * 0.12}s` }}
        />
      ))}
    </div>
  );
};

const VoiceModeModal: React.FC<VoiceModeModalProps> = ({
  isOpen,
  onClose,
  isRecording,
  isMuted = false,
  isSpeaking,
  isConnecting,
  isThinking,
  onToggleRecording,
  onEndVoiceMode,
  userText = "",
  assistantText = "",
  conversationHistory = [],
  voiceBrainBank,
}) => {
  const { selectedKnowledgeBase, knowledgeBasesList } = useKnowledgeBase();
  const { selectedHat } = useHat();
  const conversationEndRef = useRef<HTMLDivElement>(null);

  // Time since the conversation went live, for the status pill.
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!isRecording) {
      setElapsed(0);
      return;
    }
    const startedAt = Date.now();
    const timer = window.setInterval(() => setElapsed(Math.floor((Date.now() - startedAt) / 1000)), 1000);
    return () => window.clearInterval(timer);
  }, [isRecording]);

  const isSearching = !!voiceBrainBank?.searching;
  // Named for what is searched: the BrainBanks, or the one document by its file name.
  const searchingDocument =
    voiceBrainBank?.searchKind === "document" ? voiceBrainBank?.searchDocument || "" : "";
  const isOpeningBrainBank = voiceBrainBank?.searchKind === "open";
  const readingSpreadsheet = voiceBrainBank?.searchKind === "spreadsheet";
  const searchLabel = isOpeningBrainBank
    ? "Opening BrainBank…"
    : readingSpreadsheet
    ? voiceBrainBank?.searchDocument
      ? `Reading “${voiceBrainBank.searchDocument}”…`
      : "Reading spreadsheet…"
    : searchingDocument
    ? `Searching “${searchingDocument}”…`
    : "Searching BrainBanks…";

  useEffect(() => {
    conversationEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversationHistory, userText, assistantText, isSearching]);

  if (!isOpen) return null;

  const activeBrainBanks = voiceBrainBank?.brainBanks ?? [];
  const brainBankName = activeBrainBanks.map((brainBank) => brainBank.name).join(", ");
  const hasStarted = isConnecting || isRecording || conversationHistory.length > 0;

  const phase: Phase = isConnecting
    ? "connecting"
    : !isRecording
    ? "ready"
    : isMuted
    ? "muted"
    : isSearching
    ? "searching"
    : isSpeaking
    ? "speaking"
    : isThinking
    ? "thinking"
    : "listening";

  const statusTitle: Record<Phase, string> = {
    ready: "",
    connecting: "Connecting…",
    muted: "",
    searching: searchLabel,
    speaking: "Kyva is speaking",
    thinking: "Kyva is thinking…",
    listening: "Listening…",
  };
  const statusHint: Record<Phase, string> = {
    ready: "",
    connecting: "Setting up your microphone.",
    muted: "",
    searching: isOpeningBrainBank
      ? "Kyva will answer from its documents."
      : "Looking through your documents for the answer.",
    speaking: "Talk at any time to interrupt.",
    thinking: "Working on your answer.",
    listening: "Just talk. Kyva answers when you pause.",
  };

  const showLiveUser = !!userText && !conversationHistory.some((m) => m.role === "user" && m.text === userText);
  const showLiveAssistant =
    !!assistantText && !conversationHistory.some((m) => m.role === "assistant" && m.text === assistantText);
  const hasTranscript = conversationHistory.length > 0 || showLiveUser || showLiveAssistant || isSearching;

  return (
    <div className="fixed inset-0 scrim flex items-end sm:items-center justify-center z-50 sm:p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Voice Mode"
        className="bg-surface-overlay text-primary shadow-2xl ring-1 ring-black/5 dark:ring-gray-700 relative w-full h-full rounded-none sm:rounded-[20px] sm:max-w-[600px] sm:h-[85vh] sm:max-h-[760px] flex flex-col overflow-hidden"
      >
        {/* Header: title, status pill, close */}
        <div className="flex items-center justify-between pt-5 pl-5 pr-3 sm:pl-7 sm:pr-6">
          <div className="flex items-center gap-2.5">
            <span className="text-xl font-bold tracking-tight">Voice</span>
            {phase === "ready" ? (
              <span className="text-xs font-semibold text-muted bg-surface-2 rounded-full px-2.5 py-1">Not started</span>
            ) : phase === "muted" ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted bg-surface-2 rounded-full px-2.5 py-1">
                <MicOff size={12} /> Muted · {formatElapsed(elapsed)}
              </span>
            ) : phase === "connecting" ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted bg-surface-2 rounded-full px-2.5 py-1">
                <Loader2 size={12} className="animate-spin" /> Connecting
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-700 bg-red-50 dark:text-red-300 dark:bg-red-950/40 rounded-full px-2.5 py-1">
                <span className="w-[7px] h-[7px] rounded-full bg-red-600 animate-pulse" /> Live · {formatElapsed(elapsed)}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close voice mode"
            className="w-11 h-11 flex items-center justify-center rounded-xl text-muted hover:bg-surface-2 hover:text-primary transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Context: the VA and the BrainBank this conversation uses */}
        <div className="flex flex-wrap items-center gap-2 px-5 sm:px-7 pt-3.5">
          {/* Labelled with the app's own names, as in the sidebar. Before the
              conversation each chip is also its picker; once it is live they
              only show what the conversation uses. */}
          {!hasStarted ? (
            <span className="inline-flex items-center gap-1.5 h-9 pl-1 pr-3.5 rounded-full border border-border-default bg-surface-overlay text-sm text-primary max-w-full">
              <HatInput title="Virtual Assistants" panelAlign="start" />
              {selectedHat ? (
                <>
                  <span className="text-muted">Virtual Assistant:</span>
                  <span className="truncate font-semibold">{selectedHat.hat_name}</span>
                </>
              ) : (
                <span className="font-semibold">Virtual Assistants</span>
              )}
            </span>
          ) : selectedHat ? (
            <span className="inline-flex items-center gap-2 h-8 px-3 rounded-full bg-surface-2 text-[13px] text-primary max-w-full">
              <img src="/va-icon.svg" alt="" className="w-4 h-4 flex-shrink-0" />
              <span className="text-muted">Virtual Assistant:</span>
              <span className="truncate font-semibold">{selectedHat.hat_name}</span>
            </span>
          ) : null}
          {!hasStarted ? (
            <span className="inline-flex items-center gap-1.5 h-9 pl-1 pr-3.5 rounded-full border border-violet-200 bg-violet-50 dark:border-violet-800 dark:bg-violet-950/40 text-sm text-violet-800 dark:text-violet-200 max-w-full">
              <KnowledgeBaseInput title="BrainBanks" panelAlign="start" />
              {selectedKnowledgeBase ? (
                <>
                  <span className="opacity-80">BrainBank:</span>
                  <span className="truncate font-semibold">{selectedKnowledgeBase.name}</span>
                </>
              ) : (
                <span className="font-semibold">BrainBanks</span>
              )}
            </span>
          ) : activeBrainBanks.length > 0 ? (
            <span
              className="inline-flex items-center gap-2 h-8 px-3 rounded-full bg-violet-50 dark:bg-violet-950/40 text-[13px] text-violet-800 dark:text-violet-200 max-w-full"
              title={voiceBrainBank?.pinned ? "This conversation stays with this BrainBank" : undefined}
            >
              <DatabaseZap size={15} className={`flex-shrink-0 ${isSearching ? "animate-pulse" : ""}`} />
              <span className="opacity-80">{activeBrainBanks.length > 1 ? "BrainBanks:" : "BrainBank:"}</span>
              <span className="truncate font-semibold">{brainBankName}</span>
              {voiceBrainBank?.pinned && <Lock size={12} className="flex-shrink-0" aria-label="Fixed for this conversation" />}
            </span>
          ) : null}
        </div>
        {!hasStarted && (
          <p className="mx-5 sm:mx-7 mt-2 text-[13px] text-muted">
            {selectedKnowledgeBase
              ? `Kyva will answer from the ${selectedKnowledgeBase.name} BrainBank. You can also just say a BrainBank's name.`
              : knowledgeBasesList.length > 0
              ? "Pick a BrainBank to ask about your documents, or just say its name while you talk."
              : "Talk to Kyva about anything."}
          </p>
        )}

        {!hasStarted ? (
          /* Ready: one clear call to action */
          <div className="flex-grow flex flex-col items-center justify-center gap-6 px-6">
            <div className="relative w-[196px] h-[196px] flex items-center justify-center">
              <div className="absolute inset-0 rounded-full bg-violet-100 dark:bg-violet-950/50 animate-pulse" />
              <div className="absolute inset-[26px] rounded-full bg-violet-200 dark:bg-violet-900/60" />
              <div className="relative w-[92px] h-[92px] rounded-full bg-violet-700 flex items-center justify-center">
                <Mic size={38} className="text-white" />
              </div>
            </div>
            <div className="text-center flex flex-col gap-1.5">
              <div className="text-[22px] font-semibold tracking-tight">
                {selectedKnowledgeBase ? "Ask about your documents" : "Talk to Kyva"}
              </div>
              <div className="text-[15px] text-muted">Hands-free: talk naturally, and interrupt Kyva at any time.</div>
            </div>
          </div>
        ) : (
          /* Live: the conversation as chat bubbles */
          <div className="flex-grow min-h-0 overflow-y-auto px-5 sm:px-7 pt-4 flex flex-col gap-3.5" aria-live="polite">
            {!hasTranscript && (
              <p className="m-auto text-sm text-muted text-center">Your conversation will appear here.</p>
            )}
            {conversationHistory.map((message, index) =>
              message.role === "user" ? (
                <div
                  key={message.id || index}
                  className="self-end max-w-[80%] bg-violet-700 text-white px-4 py-2.5 rounded-[18px] rounded-br-md text-[15px] leading-relaxed"
                >
                  {message.text}
                </div>
              ) : (
                <div key={message.id || index} className="self-start max-w-[86%] flex flex-col gap-2">
                  <div className="bg-surface-2 px-4 py-2.5 rounded-[18px] rounded-bl-md text-[15px] leading-relaxed">
                    {message.text}
                  </div>
                  {message.interrupted && (
                    <span className="inline-flex items-center gap-1.5 pl-1.5 text-xs text-muted">
                      <CornerUpLeft size={12} /> Interrupted
                    </span>
                  )}
                  {message.sources && message.sources.length > 0 && <SourceList sources={message.sources} />}
                </div>
              )
            )}

            {showLiveUser && (
              <div className="self-end max-w-[80%] bg-violet-50 text-violet-900 dark:bg-violet-950/40 dark:text-violet-100 border border-dashed border-violet-300 dark:border-violet-700 px-4 py-2.5 rounded-[18px] rounded-br-md text-[15px] leading-relaxed">
                {userText}
                <span className="ml-0.5 animate-pulse">…</span>
              </div>
            )}

            {isSearching && (
              <div
                role="status"
                className="self-start max-w-[86%] flex items-start gap-2.5 px-3.5 py-2.5 rounded-[14px] bg-violet-50 text-violet-900 dark:bg-violet-950/40 dark:text-violet-100 text-sm"
              >
                <Loader2 size={16} className="animate-spin flex-shrink-0 mt-0.5" />
                <span className="min-w-0 break-words">{searchLabel}</span>
              </div>
            )}

            {showLiveAssistant && (
              <div className="self-start max-w-[86%] bg-surface-2 px-4 py-2.5 rounded-[18px] rounded-bl-md text-[15px] leading-relaxed">
                {assistantText}
                {isSpeaking && <span className="ml-0.5 text-violet-700 dark:text-violet-300 animate-pulse">▍</span>}
              </div>
            )}

            <div ref={conversationEndRef} />
          </div>
        )}

        {/* Status: what Kyva is doing now */}
        {hasStarted && phase !== "muted" && (
          <div className="flex flex-col items-center gap-2 px-6 pt-4">
            {phase === "speaking" ? (
              <LevelBars tone="speak" count={9} />
            ) : phase === "listening" ? (
              <LevelBars tone="listen" count={7} />
            ) : (
              <div className="h-11 flex items-center">
                <Loader2 size={28} className="animate-spin text-violet-700 dark:text-violet-300" />
              </div>
            )}
            <div className="text-base font-semibold">{statusTitle[phase]}</div>
            <div className="text-[13px] text-muted text-center">{statusHint[phase]}</div>
          </div>
        )}
        {phase === "muted" && (
          <div
            role="status"
            className="mx-5 sm:mx-7 mt-4 flex items-center gap-3 px-4 py-3.5 rounded-[14px] bg-amber-50 border border-amber-200 text-amber-900 dark:bg-amber-950/40 dark:border-amber-800 dark:text-amber-100"
          >
            <MicOff size={20} className="flex-shrink-0" />
            <div className="text-sm leading-snug">
              <strong className="font-semibold">Kyva can't hear you.</strong> Unmute to keep talking. The conversation stays open.
            </div>
          </div>
        )}

        {/* Controls */}
        {!hasStarted ? (
          <div className="flex flex-col items-center gap-3 px-6 pb-8 sm:pb-7">
            <button
              onClick={onToggleRecording}
              className="h-14 min-w-[260px] px-8 rounded-full bg-violet-700 hover:bg-violet-800 text-white text-[17px] font-semibold flex items-center justify-center gap-2.5 transition-colors active:scale-[0.98]"
            >
              <Mic size={20} /> Start talking
            </button>
            <div className="text-[13px] text-muted text-center">The conversation is saved to your chat as you talk.</div>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-4 px-6 pt-5 pb-8 sm:pb-7">
            <button
              onClick={onToggleRecording}
              disabled={isConnecting}
              aria-label={isMuted ? "Unmute microphone" : "Mute microphone"}
              aria-pressed={isMuted}
              title={isMuted ? "Unmute" : "Mute"}
              className={`w-16 h-16 rounded-full flex items-center justify-center transition-colors active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed ${
                isMuted
                  ? "bg-zinc-900 text-white hover:bg-zinc-800 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-zinc-200"
                  : "border border-border-default bg-surface-overlay text-primary hover:bg-surface-2"
              }`}
            >
              {isMuted ? <MicOff size={26} /> : <Mic size={26} />}
            </button>
            <button
              onClick={onEndVoiceMode}
              className="h-14 px-7 rounded-full bg-red-700 hover:bg-red-800 text-white text-base font-semibold flex items-center gap-2 transition-colors active:scale-[0.98]"
            >
              <PhoneOff size={18} /> End
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoiceModeModal;
